import React from 'react';

/**
 * EventDrop brand wordmark — Mode 1 Atmospheric Editorial.
 *
 * The previous logotype (cyan teardrop + camera lens + cyan "EventDrop"
 * text) was retired 2026-02 alongside the funnel migration. The brand is
 * now a serif wordmark in parchment (INK #FFE7A8), trailed by a gold
 * four-point sparkle (✦, GOLD #D4AF5E) — the same glyph that punctuates
 * every eyebrow/lede across the constitution.
 *
 * Three components are exported:
 *   • EventDropLogo       — square monogram (just the ✦ glyph at any size)
 *   • EventDropNav        — wordmark + ✦, for nav headers (clickable)
 *   • EventDropWatermark  — wordmark + ✦, smaller, for footers/watermarks
 *
 * Tokens mirror /app/design_guidelines.md.
 */

const INK   = '#FFE7A8';
const GOLD  = '#D4AF5E';
const SERIF = 'Georgia, "Times New Roman", serif';

// ✦ as the editorial monogram — gold, scales to whatever size the caller
// asks for. Replaces the legacy cyan teardrop in every "logo at size N"
// usage across the app (RSVPPage, BoothPage, GuestHome, FilmSharePage,
// PhotoDownload, etc.).
export const EventDropLogo = ({ size = 32, className = '', color = GOLD }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    className={className}
    aria-label="EventDrop"
    role="img"
  >
    {/* Four-point sparkle / star-of-bethlehem — the ✦ glyph as SVG so it
        renders identically across every browser and OS font stack. */}
    <path
      d="M16 2 L18.4 13.6 L30 16 L18.4 18.4 L16 30 L13.6 18.4 L2 16 L13.6 13.6 Z"
      fill={color}
    />
  </svg>
);

// Wordmark used in every top-nav bar. Cream serif + gold sparkle.
export const EventDropNav = ({ onClick, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    className={className}
    data-testid="eventdrop-logo"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      background: 'transparent',
      border: 'none',
      padding: 0,
      cursor: onClick ? 'pointer' : 'default',
      fontFamily: SERIF,
      color: INK,
    }}
  >
    <span
      style={{
        fontFamily: SERIF,
        fontWeight: 500,
        fontSize: 19,
        letterSpacing: '0.005em',
        color: INK,
        lineHeight: 1,
      }}
    >
      EventDrop
    </span>
    <Sparkle size={13} />
  </button>
);

// Footer/watermark variant — same wordmark, dialed down.
export const EventDropWatermark = ({ className = '' }) => (
  <div
    className={className}
    data-testid="eventdrop-watermark"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontFamily: SERIF,
      color: INK,
    }}
  >
    <span
      style={{
        fontFamily: SERIF,
        fontWeight: 500,
        fontSize: 14,
        letterSpacing: '0.005em',
        color: INK,
        lineHeight: 1,
      }}
    >
      EventDrop
    </span>
    <Sparkle size={10} />
  </div>
);

// Internal helper — the ✦ glyph at any pixel size.
const Sparkle = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <path
      d="M16 2 L18.4 13.6 L30 16 L18.4 18.4 L16 30 L13.6 18.4 L2 16 L13.6 13.6 Z"
      fill={GOLD}
    />
  </svg>
);
